const STATUS_ALIASES = {
  paid: "paid",
  settled: "paid",
  complete: "paid",
  completed: "paid",
  partial: "partial",
  partly_paid: "partial",
  partially_paid: "partial",
  part_paid: "partial",
  unpaid: "unpaid",
  due: "unpaid",
  pending: "unpaid",
  overdue: "unpaid",
  open: "unpaid",
}

const roundMoney = (value) => Math.round(Number(value || 0) * 100) / 100

const firstFiniteNumber = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined || value === "") continue
    const number = Number(value)
    if (Number.isFinite(number)) return number
  }
  return null
}

const normalizeId = (value) => {
  if (!value) return ""
  if (typeof value === "object") return String(value._id || value.id || "")
  return String(value)
}

export const getUtilityWalletBalance = (payload = {}) => {
  const balance = firstFiniteNumber(
    payload?.walletBalance,
    payload?.wallet?.walletBalance,
    payload?.wallet?.balance,
    payload?.account?.walletBalance,
    payload?.account?.balance,
    payload?.balance,
  )
  return roundMoney(balance || 0)
}

export const getUtilityDebtSummary = (summary = {}, invoices = []) => {
  const invoiceOutstanding = (Array.isArray(invoices) ? invoices : []).reduce(
    (total, invoice) => total + Number(getUtilityInvoiceSettlement(invoice).outstandingAmount || 0),
    0,
  )
  const netDebtBalance = firstFiniteNumber(
    summary?.netDebtBalance,
    summary?.outstandingAmount,
    summary?.outstanding,
    summary?.totalOutstanding,
    summary?.balanceDue,
    invoiceOutstanding,
  ) || 0
  const amountDue = firstFiniteNumber(summary?.amountDue)
  const creditAmount = firstFiniteNumber(summary?.creditAmount)

  return {
    netDebtBalance: roundMoney(netDebtBalance),
    amountDue: roundMoney(Math.max(0, amountDue === null ? netDebtBalance : amountDue)),
    creditAmount: roundMoney(Math.max(0, creditAmount === null ? -netDebtBalance : creditAmount)),
  }
}

export const normalizeUtilitySettlementStatus = (status) => {
  const normalized = String(status || "").trim().toLowerCase().replace(/[\s-]+/g, "_")
  return STATUS_ALIASES[normalized] || ""
}

export const getUtilityInvoiceSettlement = (invoice = {}) => {
  const invoiceAmount = firstFiniteNumber(invoice?.amount, invoice?.chargeAmount) || 0
  const waivedAmount = firstFiniteNumber(invoice?.waivedAmount) || 0
  const transferredAmount = firstFiniteNumber(invoice?.transferredAmount) || 0
  // Paid from credit the customer already held (prepaid, or overpaid earlier),
  // and extra cash on this bill that was moved into credit.
  const creditAppliedAmount = firstFiniteNumber(invoice?.creditAppliedAmount) || 0
  const creditMovedAmount = firstFiniteNumber(invoice?.creditMovedAmount) || 0
  const explicitCashPaid = firstFiniteNumber(invoice?.cashPaidAmount)
  const explicitTotalSettled = firstFiniteNumber(
    invoice?.totalSettledAmount,
    invoice?.settledAmount,
  )
  const legacyPaidAmount = firstFiniteNumber(invoice?.paidAmount) || 0
  const cashPaidAmount = Math.max(
    0,
    explicitCashPaid === null
      ? explicitTotalSettled === null
        ? legacyPaidAmount
        : explicitTotalSettled - waivedAmount - transferredAmount
      : explicitCashPaid,
  )
  const totalSettledAmount = Math.max(
    0,
    explicitTotalSettled === null
      ? cashPaidAmount + waivedAmount + transferredAmount + creditAppliedAmount - creditMovedAmount
      : explicitTotalSettled,
  )
  const explicitOutstanding = firstFiniteNumber(invoice?.outstandingAmount, invoice?.amountDue)
  const outstandingAmount = Math.max(
    0,
    explicitOutstanding === null ? invoiceAmount - totalSettledAmount : explicitOutstanding,
  )
  const explicitStatus = normalizeUtilitySettlementStatus(invoice?.settlementStatus)
  const status = explicitStatus || (
    outstandingAmount <= 0
      ? "paid"
      : totalSettledAmount > 0
        ? "partial"
        : "unpaid"
  )
  const dueDate = invoice?.dueDate ? new Date(invoice.dueDate) : null
  const dueDateHasPassed = dueDate && !Number.isNaN(dueDate.getTime()) && dueDate.getTime() < Date.now()

  return {
    cashPaidAmount: roundMoney(cashPaidAmount),
    waivedAmount: roundMoney(waivedAmount),
    transferredAmount: roundMoney(transferredAmount),
    creditAppliedAmount: roundMoney(creditAppliedAmount),
    creditMovedAmount: roundMoney(creditMovedAmount),
    totalSettledAmount: roundMoney(totalSettledAmount),
    outstandingAmount: roundMoney(outstandingAmount),
    settlementStatus: status,
    isOverdue: typeof invoice?.isOverdue === "boolean"
      ? invoice.isOverdue
      : status !== "paid" && Boolean(dueDateHasPassed),
  }
}

// Statement row types that need a readable name. Other types keep their own
// word (shown capitalised by the table).
const STATEMENT_ROW_TYPE_LABELS = {
  credit: "Credit",
  credit_moved: "Moved to credit",
}

export const getUtilityStatementRowTypeLabel = (type) => (
  (type === "debt_transfer" ? "Debt transferred" : STATEMENT_ROW_TYPE_LABELS[String(type || "").trim()]) || String(type || "")
)

export const getUtilitySettlementId = (row = {}) => normalizeId(
  row?.settlementId || row?.settlement?._id || row?.relatedSettlement || row?.utilitySettlement || row?.paymentId || row?._id,
)

export const getProviderUtilityCashMovement = (row = {}) => {
  const amount = roundMoney(firstFiniteNumber(row?.amount, row?.scopedAmount) || 0)
  const explicitDirection = String(row?.direction || "").trim().toLowerCase()
  if (explicitDirection === "neutral") return 0
  if (explicitDirection === "out") return roundMoney(-amount)
  if (explicitDirection === "in") return amount

  const kind = String(row?.kind || "cash").trim().toLowerCase()
  const reversalOfKind = String(row?.reversalOfKind || "").trim().toLowerCase()
  if (kind === "debt_transfer" || kind === "waiver" || (kind === "reversal" && reversalOfKind === "waiver")) return 0
  return kind === "reversal" ? roundMoney(-amount) : amount
}

const getRawUtilityAllocations = (row = {}) => (
  Array.isArray(row?.allocations)
    ? row.allocations
    : Array.isArray(row?.settlement?.allocations)
      ? row.settlement.allocations
      : []
)

export const getUtilityPaymentDescription = (row = {}) => (
  getRawUtilityAllocations(row).length > 1
    ? "Utilities payment"
    : row?.description
)

export const getVisibleUtilitySettlementRows = (rows = []) => {
  const sourceRows = Array.isArray(rows) ? rows : []
  const settlementRowCounts = new Map()
  sourceRows.forEach((row) => {
    const settlementId = normalizeId(row?.settlementId || row?.settlement?._id)
    if (!settlementId) return
    settlementRowCounts.set(settlementId, (settlementRowCounts.get(settlementId) || 0) + 1)
  })
  const canonicalSettlementIds = new Set(
    sourceRows
      .map((row) => normalizeId(row?.settlementId || row?.settlement?._id))
      .filter(Boolean),
  )
  const representedLegacyPaymentIds = new Set(
    sourceRows
      .flatMap((row) => Array.isArray(row?.allocations) ? row.allocations : [])
      .map((allocation) => normalizeId(allocation?.legacyPayment || allocation?.legacyPaymentId))
      .filter(Boolean),
  )
  const seenSettlementIds = new Set()

  return sourceRows.reduce((visibleRows, row) => {
    const settlementId = normalizeId(row?.settlementId || row?.settlement?._id)
    const linkedSettlementId = normalizeId(row?.utilitySettlement)
    const rowId = normalizeId(row?._id || row?.paymentId)
    const legacyPaymentId = normalizeId(row?.legacyPayment || row?.legacyPaymentId || row?.paymentId)

    if (linkedSettlementId && canonicalSettlementIds.has(linkedSettlementId)) return visibleRows
    if (
      representedLegacyPaymentIds.has(rowId)
      || representedLegacyPaymentIds.has(legacyPaymentId)
    ) return visibleRows
    if (settlementId) {
      if (seenSettlementIds.has(settlementId)) return visibleRows
      seenSettlementIds.add(settlementId)
    }

    const isGroupedPayment = getRawUtilityAllocations(row).length > 1 || (
      Boolean(settlementId) && settlementRowCounts.get(settlementId) > 1
    )
    const displayRow = isGroupedPayment
      ? { ...row, description: "Utilities payment" }
      : row

    // A migrated payment can contain several old wallet deductions. The API
    // returns one enriched row for each old deduction, all linked to the same
    // settlement. We show that settlement once, but its Amount Out must be the
    // full grouped payment rather than the amount from whichever child row was
    // kept first. The kept row is still the latest child, so its balance is the
    // correct balance after the whole grouped payment.
    const groupedAmount = settlementId && settlementRowCounts.get(settlementId) > 1
      ? firstFiniteNumber(row?.settlement?.amount, row?.settlement?.totalAmount, row?.totalAmount)
      : null
    const rowAmount = firstFiniteNumber(row?.amount)
    if (groupedAmount !== null && rowAmount !== null && groupedAmount !== 0 && rowAmount !== 0) {
      visibleRows.push({
        ...displayRow,
        amount: roundMoney(Math.abs(groupedAmount) * (rowAmount < 0 ? -1 : 1)),
      })
      return visibleRows
    }

    visibleRows.push(displayRow)
    return visibleRows
  }, [])
}

export const normalizeUtilityAllocations = (row = {}) => {
  const rawAllocations = getRawUtilityAllocations(row)

  return rawAllocations
    .map((allocation, index) => {
      const invoice = allocation?.invoice && typeof allocation.invoice === "object"
        ? allocation.invoice
        : null
      const amount = firstFiniteNumber(
        allocation?.amount,
        allocation?.allocatedAmount,
        allocation?.amountApplied,
      ) || 0
      const invoiceId = normalizeId(
        allocation?.invoiceId || allocation?.invoice || allocation?.relatedInvoice,
      )
      const description = String(
        allocation?.description || invoice?.description || allocation?.invoiceDescription || "",
      ).trim()
      const invoiceDate = allocation?.invoiceDate || invoice?.date || invoice?.createdAt || null
      const propertyName = allocation?.propertyName || allocation?.property?.name || ""
      const unitLabel = allocation?.unitLabel || allocation?.unit?.label || ""
      const providerName = allocation?.providerName || allocation?.provider?.name || ""

      return {
        ...allocation,
        key: String(allocation?._id || `${invoiceId || "invoice"}-${index}`),
        invoiceId,
        amount: roundMoney(amount),
        description,
        invoiceDate,
        propertyName,
        unitLabel,
        providerName,
      }
    })
    .filter((allocation) => allocation.amount > 0 || allocation.invoiceId || allocation.description)
}

export const isUtilityMeterRow = (row = {}) => {
  const source = String(row?.source || "").trim().toLowerCase()
  const type = String(row?.type || row?.eventType || "").trim().toLowerCase()
  return source === "water_meter" || type === "water_top_up" || type === "reading_update"
}

export const getUtilityRowPaymentAmount = (row = {}) => {
  if (isUtilityMeterRow(row)) return 0
  const explicitAmount = firstFiniteNumber(row?.paymentAmount)
  if (explicitAmount !== null) return roundMoney(explicitAmount)
  const type = String(row?.type || row?.eventType || "").trim().toLowerCase()
  const kind = String(row?.kind || "").trim().toLowerCase()
  const isSettlement = ["payment", "utility_payment", "settlement", "waiver", "debt_transfer"].includes(type)
    || ["cash", "waiver", "debt_transfer"].includes(kind)
  return isSettlement ? roundMoney(firstFiniteNumber(row?.amount) || 0) : 0
}

export const getUtilityRowChargeAmount = (row = {}) => {
  if (isUtilityMeterRow(row)) return 0
  const explicitAmount = firstFiniteNumber(row?.chargeAmount)
  if (explicitAmount !== null) return roundMoney(explicitAmount)
  const type = String(row?.type || row?.eventType || "").trim().toLowerCase()
  return type.includes("invoice") || type === "reversal"
    ? roundMoney(firstFiniteNumber(row?.amount) || 0)
    : 0
}
